import { createServerStateStore } from "./serverState";

export type SharePresaleStatus = "Bağlanmadı" | "Kısmi Bağlandı" | "Bağlandı" | "İptal";

export interface SharePresalePackage {
  id: number;
  sıraNo: number;
  baslik: string;
  hayvanTipi: string;
  tutar: number;
  aktif: boolean;
  not?: string;
  createdAt: string;
  updatedAt?: string;
}

export type SharePresalePaymentType = "Nakit" | "Havale";

export interface SharePresaleRecord {
  id: number;
  satisNo: string;
  tarih: string;
  musteriId?: number;
  musteriAdi: string;
  telefon: string;
  paketId?: number;
  paketBaslik: string;
  hayvanTipi: string;
  adet: number;
  birimTutar: number;
  toplamTutar: number;
  kaporaTutar: number;
  kalanTutar: number;
  odemeTipi?: SharePresalePaymentType;
  havaleNo?: string;
  linkedBankTransferIds?: string[];
  tasima?: boolean;
  grupNo?: number;
  durum: SharePresaleStatus;
  not?: string;
  linkedAnimalIds?: number[];
  linkedShareIds?: number[];
  linkedKupeNo?: string;
  linkedSiparisNo?: string;
  linkedHisseNo?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface SharePresalesState {
  packages: SharePresalePackage[];
  sales: SharePresaleRecord[];
}

const DEFAULT_STATE: SharePresalesState = {
  packages: [],
  sales: [],
};

type SharePresalesUpdater =
  | SharePresalesState
  | ((prev: SharePresalesState) => SharePresalesState);

function safeNumber(value: unknown) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeStatus(value: unknown): SharePresaleStatus {
  if (
    value === "Bağlanmadı" ||
    value === "Kısmi Bağlandı" ||
    value === "Bağlandı" ||
    value === "İptal"
  ) {
    return value;
  }

  return "Bağlanmadı";
}

function normalizePackage(item: unknown): SharePresalePackage | null {
  if (!item || typeof item !== "object") return null;
  const source = item as Partial<SharePresalePackage>;
  const id = Number(source.id);
  if (!Number.isFinite(id)) return null;

  return {
    id,
    sıraNo: safeNumber(source.sıraNo || source.id),
    baslik: String(source.baslik || "Hisse Paketi").trim() || "Hisse Paketi",
    hayvanTipi: String(source.hayvanTipi || "Dana").trim() || "Dana",
    tutar: Math.max(0, safeNumber(source.tutar)),
    aktif: source.aktif !== false,
    not: source.not ? String(source.not) : undefined,
    createdAt: String(source.createdAt || new Date().toISOString()),
    updatedAt: source.updatedAt ? String(source.updatedAt) : undefined,
  };
}

function normalizeSale(item: unknown): SharePresaleRecord | null {
  if (!item || typeof item !== "object") return null;
  const source = item as Partial<SharePresaleRecord>;
  const id = Number(source.id);
  if (!Number.isFinite(id)) return null;

  const adet = Math.max(1, safeNumber(source.adet || 1));
  const birimTutar = Math.max(0, safeNumber(source.birimTutar));
  const toplamTutar = Math.max(0, safeNumber(source.toplamTutar || adet * birimTutar));
  const kaporaTutar = Math.max(0, safeNumber(source.kaporaTutar));

  return {
    id,
    satisNo: String(source.satisNo || `HS-${id}`).trim() || `HS-${id}`,
    tarih: String(source.tarih || new Date().toISOString().slice(0, 10)),
    musteriId: Number.isFinite(Number(source.musteriId)) ? Number(source.musteriId) : undefined,
    musteriAdi: String(source.musteriAdi || "").trim(),
    telefon: String(source.telefon || "").trim(),
    paketId: Number.isFinite(Number(source.paketId)) ? Number(source.paketId) : undefined,
    paketBaslik: String(source.paketBaslik || "").trim(),
    hayvanTipi: String(source.hayvanTipi || "").trim(),
    adet,
    birimTutar,
    toplamTutar,
    kaporaTutar,
    kalanTutar: Math.max(0, toplamTutar - kaporaTutar),
    odemeTipi: source.odemeTipi === "Havale" ? "Havale" : "Nakit",
    havaleNo: source.havaleNo ? String(source.havaleNo) : undefined,
    linkedBankTransferIds: Array.isArray(source.linkedBankTransferIds)
      ? source.linkedBankTransferIds.map(String).filter(Boolean)
      : [],
    tasima: Boolean(source.tasima),
    grupNo: Number.isFinite(Number(source.grupNo)) && Number(source.grupNo) > 0 ? Number(source.grupNo) : undefined,
    durum: normalizeStatus(source.durum),
    not: source.not ? String(source.not) : undefined,
    linkedAnimalIds: Array.isArray(source.linkedAnimalIds)
      ? source.linkedAnimalIds.map(Number).filter(Number.isFinite)
      : [],
    linkedShareIds: Array.isArray(source.linkedShareIds)
      ? source.linkedShareIds.map(Number).filter(Number.isFinite)
      : [],
    linkedKupeNo: source.linkedKupeNo ? String(source.linkedKupeNo) : undefined,
    linkedSiparisNo: source.linkedSiparisNo ? String(source.linkedSiparisNo) : undefined,
    linkedHisseNo: source.linkedHisseNo ? String(source.linkedHisseNo) : undefined,
    createdAt: String(source.createdAt || new Date().toISOString()),
    updatedAt: source.updatedAt ? String(source.updatedAt) : undefined,
  };
}

function normalizeSharePresales(value: unknown): SharePresalesState {
  if (!value || typeof value !== "object") return DEFAULT_STATE;
  const source = value as Partial<SharePresalesState>;

  return {
    packages: Array.isArray(source.packages)
      ? source.packages.map(normalizePackage).filter((item): item is SharePresalePackage => Boolean(item))
      : [],
    sales: Array.isArray(source.sales)
      ? source.sales.map(normalizeSale).filter((item): item is SharePresaleRecord => Boolean(item))
      : [],
  };
}

const store = createServerStateStore<SharePresalesState>(
  "share_presales",
  DEFAULT_STATE,
  normalizeSharePresales,
  { pollIntervalMs: 10000 }
);

export function getSharePresales() {
  return store.getState();
}

export async function rehydrateSharePresales() {
  return store.rehydrate();
}

export async function setSharePresales(updater: SharePresalesUpdater) {
  return store.setState(updater);
}

export function subscribeSharePresales(listener: () => void) {
  return store.subscribe(listener);
}

export async function resetSharePresalesStore() {
  return store.reset();
}
